import { NgFor, NgIf } from '@angular/common';
import { Component, DestroyRef, Input, SimpleChanges, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { IntegrationAdderDialogComponent } from '../integration-adder-dialog/integration-adder-dialog.component';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { ZapResponse } from '../../../../models/responses/zap-response';
import { CAL_ICON, CALL_BACK_ICON, GHL_ICON,MAKE_ICON, GOOGLE_SHEET_ICON, NOTIFICATION_ICON, SMS_ICON, TELEGRAM_ICON, ZAPIER_ICON } from '../../../../app.component';
import { AiConfigurationPageEventsService } from '../../../../events/ai-configuration-page-events.service';
import { CalIntegrationDialogComponent } from '../cal-integration-dialog/cal-integration-dialog.component';
import { CalComIntegrationResponse } from '../../../../models/responses/calcom-integration-response';
import { SubscriptionHelper } from '../../../../classes/subscription-helper';
import { ZapierIntegrationDialogComponent } from '../zapier-integration-dialog/zapier-integration-dialog.component';
import { ZapService } from '../../../../services/zap.service';
import { CONFIRMATION_DIALOG_TEXT, ConfirmationDialogComponent } from '../../../general-dialogs-components/confirmation-dialog/confirmation-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiAgentService } from '../../../../services/ai-agent.service';
import { TelegramIntegrationResponse } from '../../../../models/responses/telegram-integration-response';
import { TelegramService } from '../../../../services/telegram.service';
import { TelegramIntegrationComponent } from '../telegram-integration/telegram-integration.component';
import { GoHighLevelIntegration } from '../../../../models/responses/go-high-level-integration';
import { GhlIntegrationDialogComponent } from '../ghl-integration-dialog/ghl-integration-dialog.component';
import { NotificationResponse } from '../../../../models/responses/notification-response';
import { NotificationIntegrationComponent } from '../notification-integration/notification-integration.component';
import { NotificationService } from '../../../../services/notification.service';
import { SmsSettingsResponse } from '../../../../models/responses/sms-settings-response';
import { SmsSettingDialogComponent } from '../sms-setting-dialog/sms-setting-dialog.component';
import { SmsSettingsService } from '../../../../services/sms-settings.service';
import { CallBackSettingsResponse } from '../../../../models/responses/call-back-settings-response';
import { CallBackSettingDialogComponent } from '../call-back-setting-dialog/call-back-setting-dialog.component';
import { CallBackSettingsService } from '../../../../services/call-back-settings.service';
import { GoogleSheetsIntegrationResponse } from '../../../../models/responses/google-sheets-integration-response';
import { GoogleSheetDialogComponent } from '../google-sheet-dialog/google-sheet-dialog.component';
import { GoogleSheetIntegrationService } from '../../../../services/google-sheet-integration.service';
import { MakeResponse } from '../../../../models/responses/make-response';
import { MakeIntegrationDialogComponent } from '../make-integration-dialog/make-integration-dialog.component';
import { MakeService } from '../../../../services/make.service';

// TODO fix url issue, image warning in console

export interface IntegrationView {
  id: number;
  name: string;
  icon: string;
  isEditable:boolean
  editCallBack: Function;
  deleteCallBack: Function;
}

const SMS_SETTINGS_INTEGRATION_NAME = "After call SMS settings"
const GOOGLE_SHEET_INTEGRATION_NAME = "Google sheet integration";
const CALL_BACK_SETTINGS_NAME = "Early call disconnection call back settings"

@Component({
  selector: 'app-integrations',
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormField,
    MatInput,
    MatIcon,
    MatLabel,
    MatPrefix,
    NgFor,
    MatButtonModule,
    NgIf
  ],
  templateUrl: './integrations.component.html',
  styleUrl: './integrations.component.scss'
})
export class IntegrationsComponent {
  @Input() aiAgent!: AiAgentResponse;
  integrations: IntegrationView[] = [];
  subscriptions: SubscriptionHelper;
  isLoading: boolean = false;
  filteredIntegrations: IntegrationView[] = this.integrations;

  constructor(
    private aiConfigurationPageEventsService: AiConfigurationPageEventsService,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private smsSettingsService: SmsSettingsService,
    private callBackSettingsService: CallBackSettingsService,
    private zapService: ZapService,
    private makeService:MakeService,
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    private aiAgentService: AiAgentService,
    private telegramService: TelegramService,
    private googleSheetService: GoogleSheetIntegrationService
  ) {
    this.subscriptions = new SubscriptionHelper(destroyRef);
  }

  ngOnInit() {
    this.subscribeToEvents();
    this.setIntegrationsData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['aiAgent'].currentValue) {
      this.setIntegrationsData();
    }
  }

  setIntegrationsData() {
    this.integrations = [];

    const zaps: IntegrationView[] = this.aiAgent.integrations.zaps.map(zap => {
      return {
        id: zap.id!,
        name: zap.name,
        isEditable: true,
        icon: ZAPIER_ICON,
        editCallBack: () => { this.onEditZapierIntegration(zap) },
        deleteCallBack: () => { this.onDeleteZapierIntegration(zap) },
      }
    });

    const calComIntegrations: CalComIntegrationResponse[] = this.aiAgent.integrations.calComIntegrations!;
    const makeIntegrations: MakeResponse[] = this.aiAgent.integrations.makes;
    const telegramIntegration: TelegramIntegrationResponse = this.aiAgent.integrations.telegramIntegration!;
    const ghlIntegrations: GoHighLevelIntegration[] = this.aiAgent.integrations.goHighLevelIntegration!;
    const notifications: NotificationResponse[] = this.aiAgent.integrations.notifications!;
    const googleSheetIntegration: GoogleSheetsIntegrationResponse | undefined = this.aiAgent.integrations.googleSheetsIntegraton;
    const smsSetting: SmsSettingsResponse | undefined = this.aiAgent.smsSettings;
    const callBackSetting: CallBackSettingsResponse | undefined = this.aiAgent.callBackSettings;

    if (googleSheetIntegration) {
      this.integrations.push({
        id: googleSheetIntegration.id!,
        name: GOOGLE_SHEET_INTEGRATION_NAME,
        isEditable: false,
        icon: GOOGLE_SHEET_ICON,
        editCallBack: () => { this.onEditGoogleSheetIntegration(googleSheetIntegration) },
        deleteCallBack: () => { this.onDeleteGoogleSheetIntegration(googleSheetIntegration) },
      });
    }
    if (calComIntegrations && calComIntegrations.length > 0) {
      calComIntegrations.forEach(calComIntegration => {
        this.integrations.push({
          id: calComIntegration.id!,
          name: calComIntegration.name,
          isEditable: true,
          icon: CAL_ICON,
          editCallBack: () => { this.onEditCalIntegration(calComIntegration) },
          deleteCallBack: () => { this.onDeleteCalIntegration(calComIntegration.id) },
        });
      })
    }
    if (makeIntegrations && makeIntegrations.length > 0) {
      makeIntegrations.forEach(make => {
        this.integrations.push({
          id: make.id!,
          name: make.name,
          icon: MAKE_ICON,
          isEditable: true,
          editCallBack: () => { this.onEditMakeIntegration(make) },
          deleteCallBack: () => { this.onDeleteMakeIntegration(make) },
        });
      })
    }

    if (notifications && notifications.length > 0) {
      notifications.forEach(notification => {
        this.integrations.push({
          id: notification.id!,
          name: notification.target,
          isEditable: true,
          icon: NOTIFICATION_ICON,
          editCallBack: () => { this.onEditNotificationIntegration(notification) },
          deleteCallBack: () => { this.onDeleteNotificationIntegration(notification.id) },
        });
      })
    }

    if (telegramIntegration && telegramIntegration.id) {
      this.integrations.push({
        id: telegramIntegration.id!,
        name: 'Telegram Bot',
        icon: TELEGRAM_ICON,
        isEditable: true,
        editCallBack: () => { this.onEditTelegramIntegration() },
        deleteCallBack: () => { this.onDeleteTelegramIntegration() },
      });
    }

    if (ghlIntegrations && ghlIntegrations.length > 0) {
      ghlIntegrations.forEach(ghlIntegration => {
        this.integrations.push({
          id: ghlIntegration.id!,
          name: ghlIntegration.name,
          icon: GHL_ICON,
          isEditable: true,
          editCallBack: () => { this.onEditGHLIntegration(ghlIntegration) },
          deleteCallBack: () => { this.onDeleteGHLIntegration(ghlIntegration) },
        });
      });
    }

    if (smsSetting) {
      this.integrations.push({
        id: smsSetting.id!,
        name: SMS_SETTINGS_INTEGRATION_NAME,
        icon: SMS_ICON,
        isEditable: true,
        editCallBack: () => { this.onEditSMSSettings(smsSetting) },
        deleteCallBack: () => { this.onDeleteSMSSettings(smsSetting) },
      });
    }

    if (callBackSetting) {
      this.integrations.push({
        id: callBackSetting.id!,
        name: CALL_BACK_SETTINGS_NAME,
        icon: CALL_BACK_ICON,
        isEditable: true,
        editCallBack: () => { this.onEditCallBackSettings(callBackSetting) },
        deleteCallBack: () => { this.onDeleteCallBackSettings(callBackSetting) },
      });
    }


    this.integrations = [...this.integrations, ...zaps];
    this.filteredIntegrations = this.integrations
  }

  subscribeToEvents() {
    this.subscribeToOnCalIntegrationAddedEvent();
    this.subscribeToOnZapierIntegrationAddedEvent();
    this.subscribeToOnTelegramIntegrationAddedEvent();
    this.subscribeToOnNotificationIntegrationAddedEvent();
    this.subscribeToOnSmsSettingsAddedEvent();
    this.subscribeToOnCallSettingsAddedEvent();
    this.subscribeToOnGoogleSheetIntegrationAddedEvent();
    this.subscribeToOnMakeIntegrationAddedEvent();
  }

  subscribeToOnZapierIntegrationAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onZappierIntegrationAddedEvent.subscribe((zap: ZapResponse) => {
        this.aiAgent.integrations.zaps.push(zap);
        this.setIntegrationsData();
      }));
  }

  subscribeToOnGoogleSheetIntegrationAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onGoogleSheetAddedEvent.subscribe((googleSheetIntegrationResponse: GoogleSheetsIntegrationResponse) => {
        this.aiAgent.integrations.googleSheetsIntegraton = googleSheetIntegrationResponse;
        this.setIntegrationsData();
      }));
  }



  subscribeToOnMakeIntegrationAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onMakeIntegrationAddedEvent.subscribe((make: MakeResponse) => {
        this.aiAgent.integrations.makes.push(make);
        this.setIntegrationsData();
      }));
  }


  subscribeToOnSmsSettingsAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onSmsSettingsAddedEvent.subscribe((smsSettings: SmsSettingsResponse) => {
        this.aiAgent.smsSettings = smsSettings
        this.setIntegrationsData();
      }));
  }

  subscribeToOnCallSettingsAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onCallBackSettingsAddedEvent.subscribe((callBackSetting: CallBackSettingsResponse) => {
        this.aiAgent.callBackSettings = callBackSetting
        this.setIntegrationsData();
      }));
  }


  subscribeToOnNotificationIntegrationAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onNotificationIntegrationAddedEvent.subscribe((notification: NotificationResponse) => {
        this.aiAgent.integrations.notifications.push(notification);
        this.setIntegrationsData();
      }));
  }
  subscribeToOnCalIntegrationAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onCalIntegrationAddedEvent.subscribe((cal: CalComIntegrationResponse) => {
        const index = this.aiAgent.integrations.calComIntegrations.findIndex(cl => cl.id == cal.id)

        if (index != -1)
          this.aiAgent.integrations.calComIntegrations[index] = cal;
        else
          this.aiAgent.integrations.calComIntegrations?.push(cal);

        this.setIntegrationsData();
      }));
  }

  subscribeToOnTelegramIntegrationAddedEvent() {
    this.subscriptions.add(
      this.aiConfigurationPageEventsService.onTelegramIntegrationAddedEvent.subscribe((telegram: TelegramIntegrationResponse) => {
        this.aiAgent.integrations.telegramIntegration = telegram;
        this.setIntegrationsData();
      }));
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filteredIntegrations = this.integrations.filter(agent => agent.name.toLowerCase().includes(filterValue.toLowerCase()))
  }

  onAddIntegration() {
    this.dialog.open(IntegrationAdderDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: this.aiAgent
    });
  }

  onEditCalIntegration(cal: CalComIntegrationResponse) {
    this.dialog.open(CalIntegrationDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, cal }
    });
  }

  onDeleteCalIntegration(id: number) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Cal Delete', data: 'Are you sure you want to delete this integration?' }
    }).afterClosed().subscribe((result) => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.isLoading = true;

        this.aiAgentService.removeCalComIntegration(id).subscribe({
          next: () => {
            this.aiAgent.integrations.calComIntegrations = this.aiAgent.integrations.calComIntegrations.filter(cal => cal.id != id);
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('Cal removed successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  onEditNotificationIntegration(notification: NotificationResponse) {
    this.dialog.open(NotificationIntegrationComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, notification }
    });
  }

  onDeleteNotificationIntegration(id: number) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Notification Delete', data: 'Are you sure you want to delete this integration?' }
    }).afterClosed().subscribe((result) => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.isLoading = true;

        this.notificationService.delete(id).subscribe({
          next: () => {
            this.aiAgent.integrations.notifications = this.aiAgent.integrations.notifications.filter(notif => notif.id != id);
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('Notification removed successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  onEditTelegramIntegration() {
    this.dialog.open(TelegramIntegrationComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent }
    });
  }

  onDeleteTelegramIntegration() {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Telegram Bot Delete', data: 'Are you sure you want to delete this integration?' }
    }).afterClosed().subscribe((result) => {
      this.isLoading = true;

      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.telegramService.deleteBot(this.aiAgent.integrations.telegramIntegration!.id!).subscribe({
          next: () => {
            this.aiAgent.integrations.telegramIntegration = undefined;
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('Telegram Bot removed successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  onEditZapierIntegration(zap: ZapResponse) {
    this.dialog.open(ZapierIntegrationDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, zap }
    });
  }

  onDeleteZapierIntegration(zap: ZapResponse) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Zap Delete', data: 'Are you sure you want to delete this zap?' }
    }).afterClosed().subscribe((result) => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.isLoading = true;

        this.zapService.delete(zap.id!).subscribe({
          next: () => {
            this.aiAgent.integrations.zaps = this.aiAgent.integrations.zaps.filter(z => z.id != zap.id);
            this.setIntegrationsData();
            this.isLoading = false;
            this.openSnackBar('Zap deleted successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    })
  }

  onEditMakeIntegration(make: MakeResponse) {
    this.dialog.open(MakeIntegrationDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, make }
    });
  }

  onDeleteMakeIntegration(make: MakeResponse) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Make Delete', data: 'Are you sure you want to delete this make?' }
    }).afterClosed().subscribe((result) => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.isLoading = true;

        this.makeService.delete(make.id!).subscribe({
          next: () => {
            this.aiAgent.integrations.makes = this.aiAgent.integrations.makes.filter(z => z.id != make.id);
            this.setIntegrationsData();
            this.isLoading = false;
            this.openSnackBar('Make deleted successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    })
  }

  onEditGHLIntegration(ghlIntegration: GoHighLevelIntegration) {
    this.dialog.open(GhlIntegrationDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { integrationId: ghlIntegration.id, agentId: this.aiAgent.id, calendarId: ghlIntegration.calendarId }
    });
  }

  onDeleteGHLIntegration(ghlIntegration: GoHighLevelIntegration) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'GHL Integration Delete', data: 'Are you sure you want to delete this integration?' }
    }).afterClosed().subscribe((result) => {
      this.isLoading = true;

      if (result == 'close') {
        this.aiAgentService.deleteGHLCalendar(ghlIntegration.id).subscribe({
          next: () => {
            this.aiAgent.integrations.goHighLevelIntegration = this.aiAgent.integrations.goHighLevelIntegration.filter(ghl => ghl.id != ghlIntegration.id);
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('GHL Integration removed successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  onEditSMSSettings(smsSettingsResponse: SmsSettingsResponse) {
    this.dialog.open(SmsSettingDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, smsSettings: smsSettingsResponse }
    });
  }

  onDeleteSMSSettings(smsSettings: SmsSettingsResponse) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Sms Settings Delete', data: 'Are you sure you want your after call SMS Settings?' }
    }).afterClosed().subscribe((result) => {
      this.isLoading = true;

      if (result == 'close') {
        this.smsSettingsService.deleteSmsSettigns(smsSettings.id!).subscribe({
          next: () => {
            this.aiAgent.smsSettings = undefined;
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('Sms Settings removed successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  onEditCallBackSettings(callBackSetting: CallBackSettingsResponse) {
    this.dialog.open(CallBackSettingDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, callBackSetting: callBackSetting }
    });
  }

  onDeleteCallBackSettings(callBackSetting: CallBackSettingsResponse) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Call back Settings Delete', data: 'Are you sure you want your after call Call back Settings?' }
    }).afterClosed().subscribe((result) => {
      this.isLoading = true;

      if (result == 'close') {
        this.callBackSettingsService.deleteCallBackSetting(callBackSetting.id!).subscribe({
          next: () => {
            this.aiAgent.callBackSettings = undefined;
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('Call Back Settings removed successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  onEditGoogleSheetIntegration(googleSheetIntegration: GoogleSheetsIntegrationResponse) {
    this.dialog.open(GoogleSheetDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, googleSheetIntegration: googleSheetIntegration }
    });
  }

  onDeleteGoogleSheetIntegration(googleSheetIntegration: GoogleSheetsIntegrationResponse) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Google Sheet integration Delete', data: 'Are you sure you want your after Google Sheet Integration ?' }
    }).afterClosed().subscribe((result) => {
      this.isLoading = true;

      if (result == 'close') {
        this.googleSheetService.delete(googleSheetIntegration.id!).subscribe({
          next: () => {
            this.aiAgent.integrations.googleSheetsIntegraton = undefined;
            this.setIntegrationsData()
            this.isLoading = false;
            this.openSnackBar('Google Sheet integration deleted successfully');
          },
          error: () => {
            this.isLoading = false;
            alert('Server error contact support');
          }
        })
      }
    });
  }

  private openSnackBar(message: string) {
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }
}
